import { Field, FieldArray, FieldArrayRenderProps, FieldProps, useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { IconButton, Theme } from '@mui/material';
import { PlusCircleIcon } from '@synergy/theme';
import { PoolButton, PoolTable, PoolTableColumn, PoolTableRow, PoolTextField } from '@synergy/components';
import { getCurrencySymbol, getSuffixCurrency } from '@synergy/utils';
import { makeStyles } from '@mui/styles';

import { ProductPriceFormValues } from '../models/form-values/product-price-form-values';
import { ScalePriceFormValues } from '../models/form-values/scale-price-form-values';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) => ({
    table: {
        width: '68%',
        marginBottom: theme.spacing(2),

        '& tr': {
            verticalAlign: 'top',
        },

        '& th:not(:last-child)': {
            width: '49%',
        },

        '& td:not(:last-child)': {
            width: '49%',
        },

        '& th:last-child': {
            padding: 0,
        },

        // Keeps the padding of every other cell, so the remove button sits on
        // the line of the fields instead of at the top edge of the row.
        '& td:last-child': {
            paddingRight: 16,
        },
    },
    quantityActive: {
        '& input': {
            paddingLeft: 0,
        },
    },
    addButton: {
        fontSize: 20,
    },
}));

interface ProductDetailScalePricesProps {
    currency: string;
    index: number;
}

export const ProductDetailScalePrices = (props: ProductDetailScalePricesProps) => {
    const { currency } = props;
    const intl = useIntl();
    const locale = useAppSelector(state => bestLocaleSelector(state));
    const classes = useStyles();
    const formik = useFormikContext<ProductPriceFormValues>();

    const columns: PoolTableColumn[] = [
        {
            id: 'quantity',
            label: <FormattedMessage id="app.label.quantity" />,
            sortable: false,
        },
        {
            id: 'scalePrice',
            label: <FormattedMessage id="app.product.detail.prices.scalePrice" values={{ value: '' }} />,
            sortable: false,
        },
        {
            id: 'action',
            sortable: false,
        },
    ];

    const prefix = `currencyPrices.${props.index}.scalePrices`;

    const getRows = (helper: FieldArrayRenderProps) => {
        // The mask always shows a row to type into, but it must not write one:
        // this ran during render and pushed the empty row straight into Formik's
        // values — so an untouched document reported unsaved input, and the row
        // travelled into the store from there. Formik creates the entry by itself
        // as soon as someone types in it.
        const stored: ScalePriceFormValues[] = helper.form.values.currencyPrices[props.index].scalePrices;
        const items = 0 === stored.length ? [{}] : stored;

        const handleRemove = (rowIndex: number) => {
            if (items.length === 1) {
                formik.setFieldValue(`${prefix}.${rowIndex}.quantity`, '');
                formik.setFieldValue(`${prefix}.${rowIndex}.price`, '');
                return;
            }
            helper.remove(rowIndex);
        };

        const rows: PoolTableRow[] = items.map((scalePrice, rowIndex) => {
            return {
                id: scalePrice.id || rowIndex,
                quantity: (
                    <Field name={`${prefix}.${rowIndex}.quantity`}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                value={field.value === undefined ? null : field.value}
                                fullWidth={true}
                                placeholder={intl.formatMessage({ id: 'app.product.detail.prices.beginning' })}
                                isNumeric={true}
                                noNegativeNumbers={true}
                                startAdornment={
                                    field.value ? (
                                        <FormattedMessage id="app.product.detail.prices.starting" />
                                    ) : undefined
                                }
                                className={field.value ? classes.quantityActive : undefined}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            />
                        )}
                    </Field>
                ),
                scalePrice: (
                    <Field name={`${prefix}.${rowIndex}.price`}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                value={field.value === undefined ? null : field.value}
                                placeholder={intl.formatMessage(
                                    { id: 'app.product.detail.prices.scalePrice' },
                                    { value: getCurrencySymbol(currency, locale) || undefined }
                                )}
                                fullWidth={true}
                                isNumeric={true}
                                minDecimalScale={2}
                                suffix={getSuffixCurrency(field.value, currency, locale)}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                noNegativeNumbers={true}
                            />
                        )}
                    </Field>
                ),
                action: (
                    <IconButton onClick={() => handleRemove(rowIndex)}>
                        <PlusCircleIcon color="primary" style={{ transform: 'rotate(45deg)' }} />
                    </IconButton>
                ),
            };
        });

        return rows;
    };

    return (
        <FieldArray name={prefix}>
            {helper => (
                <div>
                    <PoolTable
                        columns={columns}
                        rows={getRows(helper)}
                        styleType="bordered"
                        className={classes.table}
                    />
                    <PoolButton
                        styleType="thirdLevel"
                        onClick={() => helper.push({})}
                        startIcon={<PlusCircleIcon color="primary" className={classes.addButton} />}
                    >
                        <FormattedMessage id="app.product.detail.prices.addScalePrice" />
                    </PoolButton>
                </div>
            )}
        </FieldArray>
    );
};
